/**
 * Auto Layout — a left-to-right layered arrangement of a workflow graph.
 *
 * Hand-rolled rather than dagre/elkjs deliberately. A workflow graph is capped at 100 nodes
 * (services/workflows/graph.js MAX_NODES) and is validated acyclic and fully reachable from
 * its single `input`, so the general-purpose layered-layout machinery — cycle breaking,
 * dummy nodes for long edges, crossing minimisation — buys nothing here. A dependency would
 * also have to be added to vite.config.js's `canvas` manualChunks or it silently lands in
 * the main vendor bundle this page works hard to stay out of.
 *
 * Pure: takes nodes + edges, returns NEW nodes with new positions. Never mutates, because
 * reactflow node objects are shared with its internal store.
 */

const COLUMN_WIDTH = 260;
const ROW_HEIGHT = 130;
const ORIGIN = { x: 40, y: 40 };

/** depth = longest path from the entry, not shortest. Using the longest keeps a node strictly
 *  to the right of everything feeding it, so no edge ever points backwards; a BFS shortest
 *  path would place a node in column 1 even when a column-3 node also feeds it. */
function longestPathDepths(nodes, edges, entryId) {
  const outgoing = new Map(nodes.map((n) => [n.id, []]));
  const indegree = new Map(nodes.map((n) => [n.id, 0]));
  for (const edge of edges) {
    if (!outgoing.has(edge.source) || !indegree.has(edge.target)) continue; // dangling
    outgoing.get(edge.source).push(edge.target);
    indegree.set(edge.target, indegree.get(edge.target) + 1);
  }

  const depths = new Map();
  // Kahn's algorithm. Seeded with the entry plus any other source node, so a graph that is
  // mid-edit (an orphan the user hasn't wired up yet) still lays out instead of returning
  // half a diagram — the canvas must stay usable before validateGraph would pass.
  const queue = nodes
    .filter((n) => n.id === entryId || indegree.get(n.id) === 0)
    .map((n) => n.id);
  for (const id of queue) depths.set(id, 0);

  const remaining = new Map(indegree);
  for (let i = 0; i < queue.length; i += 1) {
    const id = queue[i];
    for (const next of outgoing.get(id) || []) {
      depths.set(next, Math.max(depths.get(next) ?? 0, (depths.get(id) ?? 0) + 1));
      remaining.set(next, remaining.get(next) - 1);
      if (remaining.get(next) === 0) queue.push(next);
    }
  }

  // Anything still unvisited sits on a cycle or behind one. validateGraph rejects cycles at
  // save time, but the canvas can hold one mid-edit — park those in a trailing column rather
  // than leaving them stacked at the origin where they'd be unfindable.
  const maxDepth = depths.size ? Math.max(...depths.values()) : 0;
  for (const node of nodes) if (!depths.has(node.id)) depths.set(node.id, maxDepth + 1);
  return depths;
}

/**
 * @returns {Array} the same nodes, in the same order, with rounded integer positions.
 *   Integers because client/src/lib/workflowGraph.js rounds before comparing — fractional
 *   values here would produce a graph that is dirty on load.
 */
export function autoLayout(nodes = [], edges = []) {
  if (!nodes.length) return nodes;
  const entry = nodes.find((n) => n.type === 'input') || nodes[0];
  const depths = longestPathDepths(nodes, edges, entry.id);

  // Within a column, keep the order the nodes already had top-to-bottom. Re-laying out a
  // graph you have hand-arranged should tidy it, not reshuffle it into an unfamiliar order.
  const columns = new Map();
  for (const node of [...nodes].sort((a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0))) {
    const depth = depths.get(node.id) ?? 0;
    if (!columns.has(depth)) columns.set(depth, []);
    columns.get(depth).push(node.id);
  }

  const tallest = Math.max(...[...columns.values()].map((c) => c.length));
  const placed = new Map();
  for (const [depth, ids] of columns) {
    // Centre each column against the tallest one, so a branch reads as symmetrical rather
    // than everything hugging the top edge.
    const offset = ((tallest - ids.length) * ROW_HEIGHT) / 2;
    ids.forEach((id, row) => placed.set(id, {
      x: Math.round(ORIGIN.x + depth * COLUMN_WIDTH),
      y: Math.round(ORIGIN.y + offset + row * ROW_HEIGHT),
    }));
  }

  return nodes.map((node) => ({ ...node, position: placed.get(node.id) || node.position }));
}

export { COLUMN_WIDTH, ROW_HEIGHT };
